import type { ExcelParseResult, ParseNotice } from '@shared/column-matching'
import type { HeadcountReconciliationResult } from '@shared/types'
import { STRUCTURED_FILE_EXTS, getFileExt } from '@shared/types'
import { computeHeadcountReconciliation } from '@shared/headcount'

// JSON is a roster too (PBI 2180), so it counts toward the one-roster rule even
// though there is no client-side JSON FTE extractor to preview it with.
const ROSTER_EXTS = new Set<string>([...STRUCTURED_FILE_EXTS, '.json'])

/** A parser notice tied to the roster file it came from. */
export type RosterNotice = ParseNotice & { fileName: string; severity: 'error' | 'warning' }

export interface RosterAnalysis {
  reconciliation: HeadcountReconciliationResult | null
  notices: RosterNotice[]
}

const NOTHING: RosterAnalysis = { reconciliation: null, notices: [] }

/**
 * Parse the single XLSX or CSV roster and report both its headcount
 * reconciliation and what the parser had to say about it.
 *
 * Only one roster reaches the pipeline (WI 2269), so with more than one roster
 * (JSON included) there is no single headcount yet and nothing is parsed — the
 * caller asks the user to choose first.
 *
 * Notices exist because a roster the parser cannot read is not rejected here —
 * without a notice the user would proceed believing the roster was understood
 * (WI 2044). A file that failed outright reports its errors only. Its warnings
 * (a skipped sheet, say) restate the same failure and used to show up beside
 * the error as a second, contradictory message.
 *
 * CSV support requires passing a parseCsv function. When omitted, CSV files are
 * skipped so call sites that lack a client-side CSV parser still work correctly.
 *
 * .json rosters are not parsed — there is no client-side JSON FTE extractor,
 * unlike xlsx (api.parseExcel) / csv (parseCsv). A single JSON roster returns no
 * reconciliation (PBI 2180 — known gap: the file still reaches the AI pipeline,
 * it just gets no pre-flight FTE estimate).
 */
export async function analyzeStructuredFiles(
  files: Array<{ name: string; base64: string }>,
  parseExcel: (base64: string) => Promise<ExcelParseResult>,
  parseCsv?: (csvText: string) => ExcelParseResult
): Promise<RosterAnalysis> {
  const rosters = files.filter((f) => ROSTER_EXTS.has(getFileExt(f.name)))
  if (rosters.length !== 1) return NOTHING

  const [roster] = rosters
  const ext = getFileExt(roster.name)
  try {
    let result: ExcelParseResult | undefined
    if (ext === '.xlsx') {
      result = await parseExcel(roster.base64)
    } else if (ext === '.csv' && parseCsv) {
      const bytes = Uint8Array.from(atob(roster.base64), (c) => c.charCodeAt(0))
      result = parseCsv(new TextDecoder().decode(bytes))
    }
    if (!result) return NOTHING

    if (result.errors.length > 0) {
      return {
        reconciliation: null,
        notices: result.errors.map((n) => ({ ...n, fileName: roster.name, severity: 'error' as const })),
      }
    }

    // A file that parses to nothing is still reconciled (0 FTE) so callers that
    // only read the reconciliation keep their panel; the notice is the addition.
    const notices: RosterNotice[] = result.data.subfunctions.length === 0
      ? [{ code: 'no-data-rows', fileName: roster.name, severity: 'error', message: 'No roles could be read from this file.' }]
      : result.warnings.map((n) => ({ ...n, fileName: roster.name, severity: 'warning' as const }))

    const ftes = result.data.subfunctions.reduce(
      (s, sf) => s + Object.values(sf.roles ?? {}).reduce((r, role) => r + (role.ftes || 0), 0),
      0
    )
    return { reconciliation: computeHeadcountReconciliation(ftes, result.sourceRowCount ?? 0), notices }
  } catch (err) {
    return {
      reconciliation: null,
      notices: [{
        code: 'no-data-rows',
        fileName: roster.name,
        severity: 'error',
        message: `This file could not be read: ${err instanceof Error ? err.message : String(err)}`,
      }],
    }
  }
}

/** Headcount reconciliation only — see {@link analyzeStructuredFiles} for the parser notices. */
export async function parseStructuredFilesForHeadcount(
  files: Array<{ name: string; base64: string }>,
  parseExcel: (base64: string) => Promise<ExcelParseResult>,
  parseCsv?: (csvText: string) => ExcelParseResult
): Promise<HeadcountReconciliationResult | null> {
  return (await analyzeStructuredFiles(files, parseExcel, parseCsv)).reconciliation
}
